/**
 * RootLayout — authenticated shell for the application: sticky topbar, a
 * collapsible sidebar with role-aware navigation, and a main `<Outlet />`.
 *
 * Phase 1 scope:
 *   - Topbar: brand on the left, simple user menu on the right (button that
 *     opens a dropdown via Radix-style details/summary; we don't want to
 *     pull a popover primitive in just for two menu items).
 *   - Sidebar: 5 nav links (Workload / Projects / Employees / Clients /
 *     Dashboard). Items not yet built in Phase 1 carry a "Скоро" badge and
 *     are visually disabled. RBAC: items hidden when user.accessLevel does
 *     not allow them — Employees/Clients are MANAGER+, Dashboard is ADMIN+.
 *   - Outlet for child routes.
 *
 * The layout reads `useMe()` for role-driven nav. While the query is still
 * loading we render the shell with a skeleton sidebar so the page doesn't
 * shift on hydration.
 */
import * as React from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Building2,
  CalendarClock,
  FileSignature,
  FolderKanban,
  FolderTree,
  LayoutDashboard,
  ListChecks,
  ListTodo,
  Settings as SettingsIcon,
  ShieldCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';

import { BrandMark } from '@/components/brand/brand-mark';
import { NotificationBell } from '@/components/notifications/notification-bell';
import { rightsRu, workNavRu } from '@/i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { isMeHydrating, useMe } from '@/hooks/use-me';
import { useLogout } from '@/hooks/use-auth';
import { canManageRights, type RightsManagerSource } from '@/lib/rights-manager';
import { cn } from '@/lib/utils';

/**
 * Access-level allow-list table for the main sidebar navigation.
 *
 * Wave-5 fix 5.x.3 / UI-review R-2: previously this was a numeric "ladder"
 * (`SUPERVISOR > ADMIN > MANAGER > EMPLOYEE`) which had two problems:
 *
 *   1. it implied SUPERVISOR sees a SUPERSET of ADMIN — which is *wrong*
 *      for Phase 1. SUPERVISOR is the org-supervision role; their entry
 *      point is the (M2-only) `/supervisor` console, NOT the operational
 *      employee/admin nav. In Phase 1 they should see only the user menu
 *      (Профиль + Выйти).
 *   2. set membership is what the product actually wants ("which roles
 *      see this nav item?"). The numeric encoding obscured the answer.
 *
 * The replacement is an explicit Set per nav item — no implicit ordering,
 * no over-ranking, no judgement calls in the comparator.
 */
type AccessLevelLiteral = 'EMPLOYEE' | 'MANAGER' | 'ADMIN' | 'SUPERVISOR';

interface NavItem {
  to: string;
  label: string;
  /** When set, item is rendered as disabled with the "Скоро" pill. */
  comingSoon?: boolean;
  /**
   * Roles that may SEE this nav item. Omitted ⇒ anyone authenticated.
   * Wave-5: explicit allow-list (was numeric `minAccessLevel`).
   */
  allowedAccessLevels?: ReadonlySet<AccessLevelLiteral>;
  /** Optional lucide icon — Phase 2 W3.T5 onward. */
  icon?: LucideIcon;
  /**
   * Exact-match active state (React Router `NavLink` `end`). Set on a PARENT
   * whose path is a prefix of a sibling nav item (`/supervisor` vs
   * `/supervisor/catalog`) so the parent is not also highlighted on the child
   * route. Omitted ⇒ default prefix-active behavior.
   */
  end?: boolean;
  /**
   * Дополнительное условие видимости поверх уровня — для пункта, который
   * уровень описывает слишком широко (Ф8 · В-Э: экран прав открыт не всякому
   * носителю уровня супервайзера, а только штатному — `canManageRights`).
   * Опущено ⇒ решает один уровень.
   */
  visibleTo?: (actor: RightsManagerSource) => boolean;
}

// Phase-1 allow-lists — derived from D2/D3 (decisions/access.md):
//   - EMPLOYEE: workload only.
//   - MANAGER (РО): workload + projects/employees/clients + dashboard
//     (Phase 5 — manager dept-scope).
//   - ADMIN: everything EXCEPT «Моя работа» (Р-59 — см. EMPLOYEE_AND_MANAGER
//     ниже; строка «ADMIN: everything» была верна до 20.08.2026 и здесь
//     поправлена, а не оставлена как «почти верная»).
//   - SUPERVISOR: /supervisor (M2) PLUS /employees as of the
//     supervisor-invites-admin feature (2026-06-10) — see
//     MANAGER_ADMIN_SUPERVISOR below. Still excluded from
//     /dashboard, /projects, /clients, /workload.
const EMPLOYEE_AND_UP: ReadonlySet<AccessLevelLiteral> = new Set(['EMPLOYEE', 'MANAGER', 'ADMIN']);
// Блок 2 · Ф4 · Р-59 — набор «у кого работа БЫВАЕТ своя». Заведён отдельным
// множеством, а не сужением `EMPLOYEE_AND_UP`: тот несёт ещё и `/workload`, где
// ГИП и техдиректор законны, и сужение утащило бы за собой чужой пункт.
//
// ⚠️ ОСНОВАНИЕ — УСТРОЙСТВО, А НЕ ВКУС. Исполнителем работы может быть только
// человек С ОТДЕЛОМ, а CHECK `user_dept_match_role` (миграция 002) требует у
// ADMIN и SUPERVISOR отдел ПУСТОЙ. Значит «Моя работа» у них пуста не «пока», а
// по построению — тот же класс, что Р-31 и Р-54: убрать, а не объяснять.
const EMPLOYEE_AND_MANAGER: ReadonlySet<AccessLevelLiteral> = new Set(['EMPLOYEE', 'MANAGER']);
const MANAGER_AND_ADMIN: ReadonlySet<AccessLevelLiteral> = new Set(['MANAGER', 'ADMIN']);
// supervisor-invites-admin (2026-06-10): /employees is the ONE main-nav
// item SUPERVISOR may see beyond /supervisor (it can invite/reissue/hard-
// delete ADMIN-tier users there). Kept distinct from MANAGER_AND_ADMIN so
// widening it never accidentally leaks /projects | /clients | /dashboard.
const MANAGER_ADMIN_SUPERVISOR: ReadonlySet<AccessLevelLiteral> = new Set([
  'MANAGER',
  'ADMIN',
  'SUPERVISOR',
]);
// Phase-2 Wave-5 W5.T3 — /supervisor is the only nav item the seeded
// sysadmin row sees in M1. Stays a singleton set so future SUPERVISOR-
// scoped pages can grow alongside it.
const SUPERVISOR_ONLY: ReadonlySet<AccessLevelLiteral> = new Set(['SUPERVISOR']);

const NAV_ITEMS: readonly NavItem[] = [
  // Phase-5 W3.T3.2: /dashboard is now LIVE — moved to FIRST nav item
  // per Q05 (mockup parity). Visible to MANAGER + ADMIN only; EMPLOYEE
  // never sees the link (route also blocks via RequireAccessLevel
  // defence-in-depth) and SUPERVISOR is excluded by the same set.
  {
    to: '/dashboard',
    label: 'Дашборд',
    allowedAccessLevels: MANAGER_AND_ADMIN,
    icon: LayoutDashboard,
  },
  // Phase-3: /workload is now LIVE (Workload Calendar + Heatmap). Server-
  // side RBAC via RequireAccessLevel; here we just mirror the EMPLOYEE-and-
  // up scope. SUPERVISOR sees inline `workload-page-forbidden` content
  // (no redirect).
  // Блок 2 · Ф0 · 0b-T3: «Загрузка» → «Загруженность». Действующая
  // редакция ТЗ (ред. 2 от 30.07.2026) называет так и §4.4 «Контроль
  // загруженности», и экран №3 в §7.1. Расходится буквальная
  // формулировка ПРИЁМКА-8 («в разделе „Загрузка“») — снимается
  // протоколом В0.5, пункт 7. Слово «Загрузка» в остальных ~15 файлах —
  // это спиннеры (loading), к пункту меню отношения не имеет.
  // Блок 2 · Ф4 · В5 — «Моя работа». Стоит ПЕРЕД «Загруженностью» намеренно:
  // для рядового сотрудника это первый экран рабочего дня («что мне делать»), а
  // загруженность отвечает на второй вопрос («сколько я на это потратил»).
  // Подпись — из словаря (`workNavRu.myWork`), а не литералом: слово «работа»
  // в Блоке 2 несущее, и второй его источник разошёлся бы молча.
  //
  // ⚠️ СКРЫТИЕ ПУНКТА — НЕ РУБЕЖ. Супервайзер сюда не попадает не потому, что
  // ссылки нет, а потому, что маршрут гейтится `RequireAccessLevel`
  // (`routes.tsx`), а сервер отвечает ему 403 по атому `work.own.read`.
  //
  // ⚠️ ⚠️ `EMPLOYEE_AND_MANAGER`, А НЕ `EMPLOYEE_AND_UP` — Р-59 (20.08.2026):
  // пункт снят У ГИПА (ADMIN) тоже, не только у техдиректора. Замер со стенда
  // под ГИПом: «Задачи 0 · Проектные работы 0 · В работе 0 · Завершённые 0 ·
  // Снятые 0» при 22 работах, где он ПОСТАНОВЩИК, — то есть экран пуст по
  // построению, а не «пока».
  //
  // ⚠️ ПУТЬ ЗАВЕДЕНИЯ ЗАДАЧИ ЭТИМ НЕ ТЕРЯЕТСЯ, И ЭТО ГРАНИЦА 1 Р-59: кнопка
  // «Новая задача» стоит и на «Поручениях» (`/tasks`, `MANAGER_AND_ADMIN` —
  // строкой ниже), открывает ТУ ЖЕ форму `task-form.modal.tsx` с полем
  // «Исполнитель», и заведённое ГИПом появляется именно там.
  //
  // ⚠️ ЭТО НЕ РУБЕЖ ТОЖЕ: рубеж — `RequireAccessLevel` в `routes.tsx`, где
  // `/my` отделён от `/tasks` в свою группу (граница 2 Р-59 — «маршрут
  // закрывается, а не прячется»).
  {
    to: '/my',
    label: workNavRu.myWork,
    allowedAccessLevels: EMPLOYEE_AND_MANAGER,
    icon: ListChecks,
  },
  // Блок 2 · Ф4 · В6-б, переименован В-ПР-2б — «Поручения» (Р-19 + Р-29). Стоит
  // СРАЗУ ЗА «Моей работой»: это соседний вопрос того же рабочего дня («чего я
  // жду от других» рядом с «что мне делать»), и разводить их «Загруженностью»
  // значило бы разорвать пару. Подпись — из словаря (`workNavRu.tasks`), оттуда
  // же берёт заголовок сам экран.
  //
  // ⚠️ СКРЫТИЕ ПУНКТА — НЕ РУБЕЖ (тот же довод, что строкой выше): рубеж стоит
  // на маршруте `RequireAccessLevel` в `routes.tsx`, а сервер отвечает
  // супервайзеру 403 по атомам `work.own.read` и `work.authored.read`.
  //
  // ⚠️ ⚠️ `MANAGER` И ВЫШЕ, А НЕ `EMPLOYEE_AND_UP` — поправка 3 к Р-29, и это
  // СЛЕДСТВИЕ, А НЕ ПОЖЕЛАНИЕ. Рядовой сотрудник вправе назначить работу только
  // самому себе (Р-16, `common/rbac/assert-assignees.ts`), а задача самому себе
  // после поправки 1 живёт ТОЛЬКО на «Моей работе» — сервер добавляет
  // `assigneeId: { not: actor.id }`. Значит «Поручения» у него пусты не «пока он
  // ничего не поручил», а ПО ПОСТРОЕНИЮ И НАВСЕГДА: пункт, никогда ничего не
  // показывающий, — не признак права, а мусор в меню.
  //
  // ⚠️ ЭТО ОТМЕНЯЕТ АБЗАЦ САМОГО Р-29 («пункт меню при этом НЕ прячется»), и
  // довод там был ВЕРЕН — «исчезнувший пункт неотличим от отнятого права», — но
  // относился к экрану, который МОЖЕТ наполниться. К этому он не относится.
  //
  // ⚠️ МАРШРУТ ЭТОЙ ПРАВКОЙ НЕ ЗАКРЫВАЕТСЯ, И ЭТО ПОЛОВИНА КРИТЕРИЯ, А НЕ
  // НЕДОДЕЛКА: `routes.tsx` остаётся `EMPLOYEE_AND_UP`. Экран для сотрудника не
  // запрещён — он БЕССОДЕРЖАТЕЛЕН, и по прямой ссылке обязан открыться и сказать
  // словами, что поручений нет (а не отдать 403 и не выглядеть сломанным).
  //
  // ⚠️ ГЕЙТ ПО УРОВНЮ, А НЕ ПО ВЫЧИСЛЕННОЙ СПОСОБНОСТИ ПОРУЧАТЬ — так решено в
  // поправке, и пограничный случай назван там же: `MANAGER` БЕЗ ОТДЕЛА поручить
  // тоже никому не может, пункт увидит, экран будет пуст всегда. Отдельного
  // правила под него НЕ ЗАВОДИТЬ: «прятать по способности» разошлось бы с
  // сервером при первой же смене полномочий.
  {
    to: '/tasks',
    label: workNavRu.tasks,
    allowedAccessLevels: MANAGER_AND_ADMIN,
    icon: ListTodo,
  },
  {
    to: '/workload',
    label: 'Загруженность',
    allowedAccessLevels: EMPLOYEE_AND_UP,
    icon: CalendarClock,
  },
  // Phase-4 W4.T5: /projects un-stubbed. Hidden for EMPLOYEE + SUPERVISOR
  // by the MANAGER_AND_ADMIN allow-list (Q18 — Supervisor 403; Q10 —
  // Employee never sees the link, only the workload they participate in).
  {
    to: '/projects',
    label: 'Проекты',
    allowedAccessLevels: MANAGER_AND_ADMIN,
    icon: FolderKanban,
  },
  // Phase-2 W3.T5: /employees is now LIVE — drop `comingSoon` so the
  // item becomes a NavLink to the page (gated server-side by
  // RequireAccessLevel). supervisor-invites-admin (2026-06-10): SUPERVISOR
  // joins MANAGER + ADMIN here so the seeded sysadmin can reach the page to
  // manage ADMIN-tier invites. Route group A mirrors this exactly.
  {
    to: '/employees',
    label: 'Сотрудники',
    allowedAccessLevels: MANAGER_ADMIN_SUPERVISOR,
    icon: Users,
  },
  // Phase-4 W4.T5: /clients un-stubbed. Same MANAGER_AND_ADMIN allow-list.
  { to: '/clients', label: 'Клиенты', allowedAccessLevels: MANAGER_AND_ADMIN, icon: Building2 },
  // Блок 2 · Ф7 · В11-b — справочник договоров. Стоит ЗА «Клиентами»
  // намеренно: договор заключается с заказчиком и без него не заводится, то
  // есть читается как продолжение той же пары справочников, а не как
  // соседний раздел.
  //
  // ⚠️ `MANAGER_AND_ADMIN`, ХОТЯ ЗАВОДИТ ДОГОВОРЫ ТОЛЬКО АДМИНИСТРАТИВНЫЙ
  // УРОВЕНЬ. Пункт открывает ЧТЕНИЕ, а читать справочник руководителю отдела
  // сервер разрешает (без суммы — её он ветвит внутри ответа). Сузить пункт
  // до пишущих значило бы отнять у него номер и срок договора, по которому
  // живут его объекты.
  //
  // ⚠️ СКРЫТИЕ ПУНКТА — НЕ РУБЕЖ: рубеж на маршруте (`routes.tsx`) и на
  // сервере. Здесь — только видимость ссылки.
  {
    to: '/contracts',
    label: 'Договоры',
    allowedAccessLevels: MANAGER_AND_ADMIN,
    icon: FileSignature,
  },
  // Phase-2 W5.T3 — Supervisor settings (SMTP / bulk-revoke / M2
  // placeholders). Visible only to the seeded sysadmin row; the route
  // itself is gated by RequireAccessLevel({allowed:['SUPERVISOR']}) so
  // a deep-linked navigation by anyone else lands on /forbidden.
  {
    to: '/supervisor',
    label: 'Настройки супервайзера',
    allowedAccessLevels: SUPERVISOR_ONLY,
    icon: SettingsIcon,
    // Exact match so this parent link is not co-highlighted on
    // /supervisor/catalog (the sibling nav item below).
    end: true,
  },
  // Block 2 «Трекер объектов», Этап 2 · W2a · T2b — the §5.2 catalog editor.
  // SUPERVISOR-only (route also gated by RequireAccessLevel({['SUPERVISOR']}));
  // WRITES are SUPERVISOR-only server-side, READ (the picker tree) is wider.
  {
    to: '/supervisor/catalog',
    label: 'Каталог разделов',
    allowedAccessLevels: SUPERVISOR_ONLY,
    icon: FolderTree,
  },
  // Блок 2 · Ф8 · В-Э — сводный список прав (Р-200, вариант Б: «у супервайзера
  // — сводный список»). Стоит последним в группе супервайзера: настройки →
  // справочник → права.
  //
  // ⚠️ УРОВНЯ ЗДЕСЬ МАЛО, ПОЭТОМУ `visibleTo`. Маршруты прав на сервере
  // закрыты порогом «уровень супервайзера И неназначаемая должность»
  // (умолчание 4 Р-211): спящая должность `CEO` того же уровня не проходит.
  // Пункт, показанный по одному уровню, вёл бы её на страницу, единственный
  // запрос которой отвечает 403.
  //
  // ⚠️ СКРЫТИЕ ПУНКТА — НЕ РУБЕЖ: рубеж на маршруте (`routes.tsx`,
  // `RequireRightsManager`) и на сервере.
  {
    to: '/supervisor/rights',
    label: rightsRu.page.nav,
    allowedAccessLevels: SUPERVISOR_ONLY,
    visibleTo: canManageRights,
    icon: ShieldCheck,
  },
] as const;

const NUMERIC_ACCESS_LEVEL: Record<number, AccessLevelLiteral> = {
  1: 'EMPLOYEE',
  2: 'MANAGER',
  3: 'ADMIN',
  4: 'SUPERVISOR',
};

/**
 * Normalise the heterogeneous `accessLevel` shape coming from `useMe()`.
 * The Phase-1 `MeUser.accessLevel` field can be either the canonical
 * string union (BE → JSON) or a legacy number (Batch-1 type drift —
 * cleaned up in Wave-5 fix 5.x.10). Accept both at runtime.
 */
function normaliseAccessLevel(
  accessLevel: string | number | undefined | null,
): AccessLevelLiteral | null {
  if (accessLevel === undefined || accessLevel === null) return null;
  if (typeof accessLevel === 'string') {
    return (
      (['EMPLOYEE', 'MANAGER', 'ADMIN', 'SUPERVISOR'] as AccessLevelLiteral[]).find(
        (v) => v === accessLevel,
      ) ?? null
    );
  }
  return NUMERIC_ACCESS_LEVEL[accessLevel] ?? null;
}

/**
 * Decide whether the given user role may see the nav item.
 *
 *   - item without `allowedAccessLevels` → visible to anyone authenticated
 *   - item with the set → visible iff the user's role is IN the set
 *
 * supervisor-invites-admin (2026-06-10): SUPERVISOR is now present in the
 * /employees and /supervisor nav sets (and ONLY those two), so this
 * function returns `true` for those items and `false` for the rest
 * (/dashboard, /workload, /projects, /clients) under the SUPERVISOR role.
 */
function canSeeNavItem(
  item: NavItem,
  userAccessLevel: string | number | undefined,
  // Ф8 · В-Э: признак должности нужен только пунктам с `visibleTo`. Без него
  // такой пункт ЗАКРЫТ — «не знаю» не читается как «можно».
  userIsAssignable?: boolean | null,
): boolean {
  const normalised = normaliseAccessLevel(userAccessLevel);
  if (item.allowedAccessLevels) {
    if (!normalised) return false;
    if (!item.allowedAccessLevels.has(normalised)) return false;
  }
  if (item.visibleTo) {
    return item.visibleTo({ accessLevel: normalised, isAssignable: userIsAssignable ?? null });
  }
  return true;
}

function ComingSoonBadge(): React.ReactElement {
  return (
    <span
      data-testid="coming-soon-badge"
      className="ml-auto rounded-full bg-warn-wash px-1.5 py-0.5 text-[10px] font-medium uppercase text-warn"
    >
      Скоро
    </span>
  );
}

/**
 * UserMenu — Wave-5 fix 5.x.4 / UI-review C-3.
 *
 * Built on Radix DropdownMenu so we inherit Esc-to-close, Tab+Arrow
 * keyboard navigation, return-focus on close, and proper menu/menuitem
 * ARIA roles for free. The previous hand-rolled `<div>` popover had none
 * of those affordances and failed the accessibility baseline.
 */
function UserMenu(): React.ReactElement {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const logout = useLogout();

  const initials = React.useMemo(() => {
    if (!me?.email) return '·';
    return me.email.slice(0, 2).toUpperCase();
  }, [me?.email]);

  const handleLogout = (): void => {
    logout.mutate(undefined, {
      onSettled: () => navigate('/login', { replace: true }),
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="user-menu-trigger"
          className="flex items-center gap-2 rounded-full border border-line bg-surface px-2 py-1 text-sm hover:bg-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <span
            aria-hidden
            className="grid h-7 w-7 place-items-center rounded-full bg-accent text-[11px] font-semibold text-accent-foreground"
          >
            {initials}
          </span>
          <span className="hidden md:inline text-ink">{me?.email ?? '…'}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        data-testid="user-menu"
        // Width tuned to match the prior hand-rolled menu so the visual
        // delta is just the a11y improvement, not a redesign.
        className="w-48"
      >
        <DropdownMenuItem
          asChild
          // `select` fires before the link navigates, so we don't need a
          // click handler — Radix closes the menu and the Link handles
          // the navigation.
        >
          <Link to="/profile">Профиль</Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={handleLogout}
          disabled={logout.isPending}
          // Logout is a button-like action; we render it inline rather
          // than via asChild to avoid the disabled-pointer-events
          // interaction pitfall with anchors.
        >
          Выйти
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Sidebar({
  accessLevel,
  isAssignable,
}: {
  /** `Role.isAssignable` — для пунктов с `visibleTo` (Ф8 · В-Э). */
  isAssignable?: boolean | null;
  // Wave-5 fix 5.x.10: `MeUser.accessLevel` is now the string union from
  // @aris/shared. Number is still accepted to preserve compatibility
  // with cached data from before the type tightening.
  accessLevel: AccessLevelLiteral | string | number | undefined;
}): React.ReactElement | null {
  const items = NAV_ITEMS.filter((it) => canSeeNavItem(it, accessLevel, isAssignable));
  // Live-VPS UI review (2026-05-08, defect #3): when the role-aware
  // filter leaves zero visible items (SUPERVISOR in Phase 1) the rail
  // was still rendered as 224px of empty `bg-side-bg` next to the main
  // content. Bail out entirely so `<main>` stretches to full width and
  // there is no "where's the nav?" UX dead zone. The user menu in the
  // topbar remains the supervisor's only navigation surface.
  if (items.length === 0) return null;
  // Wave-5 fix 5.x.7: classes use the dedicated --side-* tokens instead
  // of the generic --bg / --line / --ink so the rail visually separates
  // from page content (warm-theme reference, theme-warm.html L695-728).
  return (
    <nav
      aria-label="Основная навигация"
      data-testid="sidebar-nav"
      className="hidden md:flex w-56 shrink-0 flex-col gap-0.5 border-r border-side-border bg-side-bg p-3"
    >
      {items.map((item) => {
        const disabled = Boolean(item.comingSoon);
        const baseCls =
          'flex items-center gap-2 rounded-sm px-3 py-2 text-sm transition-colors duration-short';
        const Icon = item.icon;
        // Phase 5 post-deploy F-04: testid renamed from `nav-{slug}` to
        // `sidebar-nav-{slug}` so live-VPS spec S5 (Phase 5 dashboard)
        // can resolve individual sidebar items via the prefix selector
        // `[data-testid^="sidebar-nav-"]`. The container nav already
        // uses `sidebar-nav`; per-item ids now share the same prefix.
        if (disabled) {
          return (
            <span
              key={item.to}
              data-testid={`sidebar-nav-${item.to.slice(1)}`}
              aria-disabled
              className={cn(baseCls, 'text-side-muted cursor-not-allowed')}
            >
              {Icon ? <Icon className="size-4" aria-hidden="true" /> : null}
              {item.label}
              <ComingSoonBadge />
            </span>
          );
        }
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`sidebar-nav-${item.to.slice(1)}`}
            className={({ isActive }) =>
              cn(
                baseCls,
                'text-side-ink hover:bg-side-hover-bg',
                // Active row: white surface above the FBF7F1 rail — pops
                // exactly the way the warm-theme reference renders it.
                isActive && 'bg-side-active-bg text-side-active-ink shadow-1',
              )
            }
          >
            {Icon ? <Icon className="size-4" aria-hidden="true" /> : null}
            {item.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

export interface RootLayoutProps {
  children?: React.ReactNode;
}

/** Sidebar placeholder shown while the actor's `accessLevel` is still
 *  hydrating. Without it the role-aware `canSeeNavItem` filter runs against
 *  an undefined level, hides every gated item, and the rail flashes empty
 *  (or collapses) until `/auth/me` lands — then pops the real nav in. The
 *  placeholder keeps the rail's footprint stable across the window
 *  (fix-pack-2 item 2). */
function SidebarSkeleton(): React.ReactElement {
  return (
    <aside
      data-testid="sidebar-nav-skeleton"
      aria-hidden="true"
      className="hidden w-56 shrink-0 flex-col gap-2 border-r border-side-border bg-side-bg p-3 md:flex"
    >
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
    </aside>
  );
}

/**
 * Классы главной области содержимого.
 *
 * ═══ `min-w-0` ЗДЕСЬ — НЕСУЩЕЕ, А НЕ КОСМЕТИКА ═══════════════════════════
 *
 * `<main>` — элемент флекс-строки, а у элемента флекса `min-width` по
 * умолчанию `auto`: он НЕ МОЖЕТ стать уже своего содержимого. Широкая сетка
 * внутри (график занятости, календарь) распирала `<main>`, и вбок ехала ВСЯ
 * страница — вместе с колонкой имён, вкладками и панелью фильтров: прокрутив
 * к концу месяца, пользователь терял из виду, чей это график. Замер на стенде
 * 10.08.2026: документ 19 865 px при окне 1440 px. Собственная область
 * прокрутки у сетки при этом была, но не срабатывала — распирался родитель.
 *
 * ⚠️ ПОБОЧНОЕ ДЕЙСТВИЕ НАЗВАНО НАМЕРЕННО: то же самое чинится и у
 * календарной сетки (`calendar-grid.tsx`, тоже `min-w-max` внутри своего
 * `overflow-auto`). Обе широкие сетки приложения — и таблицы
 * (`data-table.tsx`, `overflow-x-auto`) — несут собственный контейнер
 * прокрутки, поэтому обрезки содержимого без пути к нему нигде не возникает.
 *
 * Вынесено константой, чтобы правка классов главной области была ОДНИМ
 * местом и читалась в истории отдельно от прочей вёрстки оболочки: снятие
 * `min-w-0` — возврат дефекта, а не косметика. Проверяется браузерной спекой
 * `e2e/gantt-legibility.spec.ts`, которая ходит на `/workload` через ЭТУ
 * оболочку, а не через свою копию.
 */
export const MAIN_CLASS = 'min-w-0 flex-1 p-6';

export function RootLayout({ children }: RootLayoutProps): React.ReactElement {
  const { data: me, isLoading } = useMe();
  // Hold the sidebar in a skeleton while the actor is loading OR present
  // but not yet carrying `accessLevel` (fix-pack-2 item 2) — otherwise the
  // role-gated nav renders empty during the un-hydrated window and then
  // flickers in. Once `accessLevel` arrives the real role-filtered rail
  // renders. With the BE fix-pack-2 the partial shape no longer reaches the
  // cache; this keeps the rail flicker-free for the loading window too.
  const navHydrating = isLoading || isMeHydrating(me);
  return (
    <div data-testid="root-layout" className="flex min-h-screen flex-col bg-bg text-ink">
      <header
        data-testid="topbar"
        className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-line bg-surface px-4"
      >
        <Link
          to="/"
          aria-label="АРИС — на главную"
          className="select-none rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <BrandMark size="sm" withText />
        </Link>
        <div className="flex items-center gap-2">
          {/* Блок 2 · Ф7 · В11-a (Р-132). Колокольчик сам решает, рисоваться ли:
              супервайзеру и неавторизованному состоянию он не показывается. */}
          {isLoading ? null : <NotificationBell />}
          {isLoading ? <Skeleton className="h-7 w-24 rounded-full" /> : <UserMenu />}
        </div>
      </header>
      <div className="flex flex-1">
        {navHydrating ? (
          <SidebarSkeleton />
        ) : (
          <Sidebar accessLevel={me?.accessLevel} isAssignable={me?.isAssignable} />
        )}
        <main className={MAIN_CLASS}>{children ?? <Outlet />}</main>
      </div>
    </div>
  );
}

// Exposed for tests / future use.
export { NAV_ITEMS, canSeeNavItem };
